import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { View, Text, Pressable, StyleSheet, Platform } from 'react-native';
import { Bot, Menu, X, User, Wrench, ClipboardList, Calendar, DollarSign, Settings, Package, Shield, Building, Layout, MessageSquare, Users } from '../lib/icons';
import { shadows } from '../lib/styles';
import { useMobileContainer } from './MobileContainer';

interface NavbarProps {
  isSystemManager?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ isSystemManager = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const { isInMobileContainer, isDesktop } = useMobileContainer();

  const NavLink = ({ to, icon: Icon, label, iconSize = 24 }: { to: string; icon: any; label: string; iconSize?: number }) => {
    const isActive = location.pathname === to;
    return (
      <Pressable onPress={() => navigate(to)} style={styles.navLink}>
        <View style={styles.navLinkContent}>
          <Icon size={iconSize} color={isActive ? '#000000' : '#6b7280'} />
          <Text style={[styles.navLinkText, isActive && styles.navLinkTextActive]}>
            {label}
          </Text>
        </View>
      </Pressable>
    );
  };

  const MenuLink = ({ to, icon: Icon, label, onPress }: { to: string; icon: any; label: string; onPress: () => void }) => {
    return (
      <Pressable 
        onPress={() => {
          navigate(to);
          onPress();
        }} 
        style={styles.menuLink}
      >
        <View style={styles.menuLinkContent}>
          <Icon size={20} color="#6b7280" />
          <Text style={styles.menuLinkText}>{label}</Text>
        </View>
      </Pressable>
    );
  };

  return (
    <View style={[
      styles.navbar,
      isInMobileContainer && isDesktop && styles.navbarContained
    ]}>
      <View style={styles.navbarContent}>
        <NavLink to="/dashboard" icon={Layout} label="Dashboard" iconSize={24} />
        <NavLink to="/my-home" icon={Building} label="My Homes" iconSize={24} />
        <NavLink to="/honeydolist" icon={ClipboardList} label="The List" iconSize={24} />
        <NavLink to="/conversations" icon={MessageSquare} label="Messages" iconSize={24} />
        <Pressable
          onPress={() => setIsMenuOpen(!isMenuOpen)}
          style={styles.menuButton}
        >
          <View style={styles.navLinkContent}>
            {isMenuOpen ? (
              <X size={24} color="#6b7280" />
            ) : (
              <Menu size={24} color="#6b7280" />
            )}
            <Text style={styles.navLinkText}>Menu</Text>
          </View>
        </Pressable>

        {/* Menu Dropup */}
        {isMenuOpen && (
          <>
            {/* Backdrop */}
            <Pressable
              style={styles.backdrop}
              onPress={() => setIsMenuOpen(false)}
            />
            
            {/* Menu Content */}
            <View style={styles.menuContent}>
              <View style={styles.menuItems}>
                <MenuLink
                  to="/service-requests"
                  icon={Wrench}
                  label="Service Requests"
                  onPress={() => setIsMenuOpen(false)}
                />
                <MenuLink
                  to="/maintenance-plan"
                  icon={Calendar}
                  label="Maintenance Plan"
                  onPress={() => setIsMenuOpen(false)}
                />
                <MenuLink
                  to="/visits"
                  icon={Calendar}
                  label="Visits"
                  onPress={() => setIsMenuOpen(false)}
                />
                <MenuLink
                  to="/expenses"
                  icon={DollarSign}
                  label="Track Expenses"
                  onPress={() => setIsMenuOpen(false)}
                />
                <MenuLink
                  to="/inventory"
                  icon={Package}
                  label="Inventory"
                  onPress={() => setIsMenuOpen(false)}
                />
                <MenuLink
                  to="/managed-services"
                  icon={Settings}
                  label="Managed Services"
                  onPress={() => setIsMenuOpen(false)}
                />
                <MenuLink
                  to="/team"
                  icon={Users}
                  label="Your Team"
                  onPress={() => setIsMenuOpen(false)}
                />
                <MenuLink
                  to="/profile"
                  icon={User}
                  label="Profile"
                  onPress={() => setIsMenuOpen(false)}
                />
                {isSystemManager && (
                  <MenuLink
                    to="/admin"
                    icon={Shield}
                    label="Admin"
                    onPress={() => setIsMenuOpen(false)}
                  />
                )}
              </View>
              
              {/* Version Number */}
              <View style={styles.versionContainer}>
                <Text style={styles.versionText}>v0.1.9</Text>
              </View>
            </View>
          </>
        )}
      </View>
    </View>
  );
};

const styles = StyleSheet.create({
  navbar: {
    backgroundColor: '#ffffff',
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
    ...(Platform.OS === 'web' && {
      position: 'fixed' as any,
      bottom: 0,
      left: 0,
      right: 0,
      zIndex: 50,
    }),
    ...(Platform.OS !== 'web' && {
      // On native, the navbar is positioned at the bottom by the parent layout
    }),
  },
  navbarContained: {
    ...(Platform.OS === 'web' && {
      position: 'absolute' as any,
      left: 0,
      right: 0,
      bottom: 0,
    }),
  },
  navbarContent: {
    flexDirection: 'row',
    justifyContent: 'space-around',
    alignItems: 'center',
    height: 64,
    paddingHorizontal: 8,
  },
  navLink: {
    // No styles needed for Pressable
  },
  navLinkContent: {
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: 56,
  },
  navLinkText: {
    fontSize: 10,
    fontWeight: '500',
    marginTop: 4,
    color: '#6b7280',
  },
  navLinkTextActive: {
    color: '#000000',
  },
  menuButton: {
    width: 56,
  },
  backdrop: {
    ...(Platform.OS === 'web' && {
      position: 'fixed' as any,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
    }),
    ...(Platform.OS !== 'web' && {
      position: 'absolute',
      top: -1000,
      left: -1000,
      right: -1000,
      bottom: -1000,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
    }),
  },
  menuContent: {
    ...shadows.bottom,
    ...(Platform.OS === 'web' && {
      position: 'absolute',
      bottom: '100%',
      left: 0,
      right: 0,
      backgroundColor: '#ffffff',
      borderTopWidth: 1,
      borderTopColor: '#e5e7eb',
      borderTopLeftRadius: 16,
      borderTopRightRadius: 16,
    }),
    ...(Platform.OS !== 'web' && {
      position: 'absolute',
      bottom: 64,
      left: 0,
      right: 0,
      backgroundColor: '#ffffff',
      borderTopWidth: 1,
      borderTopColor: '#e5e7eb',
      borderTopLeftRadius: 16,
      borderTopRightRadius: 16,
    }),
  },
  menuItems: {
    paddingVertical: 16,
  },
  menuLink: {
    // No styles needed for Pressable
  },
  menuLinkContent: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 24,
    paddingVertical: 12,
  },
  menuLinkText: {
    marginLeft: 12,
    fontWeight: '500',
    color: '#6b7280',
    fontSize: 16,
  },
  versionContainer: {
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#f3f4f6',
    alignItems: 'center',
  },
  versionText: {
    fontSize: 10,
    color: '#9ca3af',
    fontWeight: '400',
  },
});

export default Navbar;
